import * as THREE from "three";
import realWoodUrl from "../assets/ui/texture.webp";

const realWoodLoader = new THREE.TextureLoader();

/**
 * Charge une instance independante (son propre `repeat`/`offset`, reglable
 * par appelant) de la photo de grain de bois reelle deja utilisee par les
 * panneaux HUD en CSS (`src/assets/ui/texture.webp`) : plus de detail/
 * realisme qu'un grain procedural, pour l'echiquier et la table.
 *
 * `repeat` < 1 cadre sur un seul extrait (pas de raccord de repetition
 * visible) ; `offset` deplace cet extrait pour eviter les zones a fort
 * contraste de la photo (reflet clair en diagonale, coins tres sombres) qui
 * sinon se comportent comme un eclairage "cuit" en plus de l'eclairage
 * reel de la scene, et rendent le rendu final inegalement sombre/tachete.
 */
export function loadRealWoodTexture(
  repeatX = 1,
  repeatY = 1,
  offsetX = 0,
  offsetY = 0,
): THREE.Texture {
  const texture = realWoodLoader.load(realWoodUrl);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.offset.set(offsetX, offsetY);
  return texture;
}

/** Bruit de grain de bois (lignes ondulantes + noeuds), genere par canvas 2D. */
export function createWoodTexture(baseColor: string, grainColor: string, size = 256): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = grainColor;
  ctx.globalAlpha = 0.35;
  const lineCount = 22;
  for (let i = 0; i < lineCount; i++) {
    const y = (i / lineCount) * size + (Math.random() - 0.5) * 6;
    ctx.beginPath();
    ctx.lineWidth = 1 + Math.random() * 2;
    ctx.moveTo(0, y);
    for (let x = 0; x <= size; x += size / 8) {
      const wobble = Math.sin(x * 0.05 + i) * 4 + (Math.random() - 0.5) * 3;
      ctx.lineTo(x, y + wobble);
    }
    ctx.stroke();
  }

  ctx.globalAlpha = 0.12;
  for (let i = 0; i < 40; i++) {
    ctx.beginPath();
    ctx.fillStyle = Math.random() > 0.5 ? grainColor : baseColor;
    ctx.ellipse(
      Math.random() * size,
      Math.random() * size,
      2 + Math.random() * 6,
      1 + Math.random() * 2,
      Math.random() * Math.PI,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/**
 * Bois "rustique" pour les pieces : grain plus marque et irregulier que le bois du
 * plateau/de la table (traits plus larges, noeuds plus sombres, quelques eraflures),
 * comme du bois brut sculpte a la main plutot que verni/poli.
 */
export function createRusticWoodTexture(baseColor: string, grainColor: string, knotColor: string, size = 256): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);

  // Veines de grain principales, epaisses et irregulieres.
  ctx.globalAlpha = 0.4;
  const lineCount = 16;
  for (let i = 0; i < lineCount; i++) {
    const y = (i / lineCount) * size + (Math.random() - 0.5) * 8;
    ctx.beginPath();
    ctx.strokeStyle = grainColor;
    ctx.lineWidth = 1.5 + Math.random() * 3.5;
    ctx.moveTo(0, y);
    for (let x = 0; x <= size; x += size / 10) {
      const wobble = Math.sin(x * 0.045 + i * 1.7) * 6 + (Math.random() - 0.5) * 5;
      ctx.lineTo(x, y + wobble);
    }
    ctx.stroke();
  }

  // Fines fibres secondaires, plus serrees et plus claires, pour casser la regularite.
  ctx.globalAlpha = 0.15;
  for (let i = 0; i < 30; i++) {
    const y = Math.random() * size;
    ctx.beginPath();
    ctx.strokeStyle = grainColor;
    ctx.lineWidth = 0.6 + Math.random();
    ctx.moveTo(0, y);
    for (let x = 0; x <= size; x += size / 6) {
      ctx.lineTo(x, y + (Math.random() - 0.5) * 10);
    }
    ctx.stroke();
  }

  // Noeuds de bois (cercles concentriques sombres).
  ctx.globalAlpha = 0.55;
  const knotCount = 3 + Math.floor(Math.random() * 2);
  for (let i = 0; i < knotCount; i++) {
    const cx = Math.random() * size;
    const cy = Math.random() * size;
    const radius = 4 + Math.random() * 7;
    for (let r = radius; r > 0; r -= 1.6) {
      ctx.beginPath();
      ctx.strokeStyle = knotColor;
      ctx.lineWidth = 0.8;
      ctx.ellipse(cx, cy, r, r * 0.7, Math.random() * 0.6, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // Petites eraflures/taches d'usure eparses.
  ctx.globalAlpha = 0.1;
  for (let i = 0; i < 50; i++) {
    ctx.beginPath();
    ctx.fillStyle = Math.random() > 0.5 ? knotColor : "#000000";
    ctx.ellipse(
      Math.random() * size,
      Math.random() * size,
      1 + Math.random() * 3,
      0.5 + Math.random() * 1.5,
      Math.random() * Math.PI,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/**
 * Variante en niveaux de gris de la meme trame que `createRusticWoodTexture`, utilisee
 * comme `bumpMap` pour donner du relief au grain (fibres et noeuds legerement creuses)
 * sous l'eclairage, plutot qu'un aplat de couleur parfaitement lisse.
 */
export function createWoodBumpTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, size, size);

  const lineCount = 16;
  for (let i = 0; i < lineCount; i++) {
    const y = (i / lineCount) * size + (Math.random() - 0.5) * 8;
    ctx.beginPath();
    ctx.strokeStyle = Math.random() > 0.5 ? "#3a3a3a" : "#b8b8b8";
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 1.5 + Math.random() * 3;
    ctx.moveTo(0, y);
    for (let x = 0; x <= size; x += size / 10) {
      const wobble = Math.sin(x * 0.045 + i * 1.7) * 6 + (Math.random() - 0.5) * 5;
      ctx.lineTo(x, y + wobble);
    }
    ctx.stroke();
  }

  ctx.globalAlpha = 0.6;
  const knotCount = 3;
  for (let i = 0; i < knotCount; i++) {
    const cx = Math.random() * size;
    const cy = Math.random() * size;
    const radius = 4 + Math.random() * 7;
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    gradient.addColorStop(0, "#1a1a1a");
    gradient.addColorStop(1, "#808080");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(cx, cy, radius, radius * 0.7, Math.random() * 0.6, 0, Math.PI * 2);
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}
